// CTV-775: the AI commercials of one surface (wizard creative step or the
// Edit commercial tab) and which one is ACTIVE (has the WaymarkProvider
// mounted). Replaces the CTV-653 useSwapWaymarkSession.
//
// Rules (CONTEXT.md, "Waymark improvements"):
//   - Committed is DERIVED: a slot on the surface carries the commercial's id
//     (`committedIds`). After save the creatives row references it, and the
//     swap list endpoint already excludes those server-side.
//   - CTV-785: "ever committed" is committed now OR `firstCommittedAt` set
//     (the server stamps it on the first draft save that carries the pointer;
//     `recordCommitted` mirrors it locally the moment a slot is stamped). A
//     commercial that was ever in the campaign is finished business: never
//     active again, and its receipt row offers Add to campaign when no slot
//     carries it any more.
//   - Active = the newest commercial never yet in the campaign, else none.
//     One provider per surface, keyed on `activeId` by the caller. Review on
//     a finished commercial opens a side editor session
//     (useWaymarkEditorSession) by its own token (`tokenOf`).
//   - `create()` always inserts a new row (Make another / the first one) and
//     makes it active. Start over and Review never call it.
//   - The listed snapshot goes stale as soon as the provider persists state;
//     `recordState` keeps it current so the rules never run on stale data.
//
// Two modes share one hook so both surfaces read the same rules:
//   wizard: `hydrated` comes from the draft load (`draft.commercials[]`);
//           create = POST /waymark/commercials { slug, draftToken }.
//   swap:   loads via POST /waymark/commercials/list-for-submission the first
//           time `active` flips true (the modal latches activation), and
//           create = POST /waymark/commercials/for-submission.

import React from 'react';
import { apiClient } from '../../../lib/apiClient';
import type { WaymarkInitial } from './context';

export interface AiCommercial {
  id: string;
  token: string;
  initial: WaymarkInitial;
  createdAt?: string;
  /** CTV-785: ISO time a creative slot first carried it; null until then. */
  firstCommittedAt?: string | null;
}

/** CTV-785: a finished commercial for the campaign receipt (see receipt.ts). */
export interface FinishedCommercial {
  id: string;
  ordinal: number;
  renderedUrl: string | null;
  videoId: string | null;
  everCommitted: boolean;
}

interface CommercialPatch {
  initial?: Partial<WaymarkInitial>;
  firstCommittedAt?: string | null;
}

function applyPatch(c: AiCommercial, patch: CommercialPatch | undefined): AiCommercial {
  if (!patch) return c;
  return {
    ...c,
    initial: patch.initial ? { ...c.initial, ...patch.initial } : c.initial,
    firstCommittedAt:
      patch.firstCommittedAt !== undefined ? patch.firstCommittedAt : c.firstCommittedAt,
  };
}

type Args =
  | {
      surface: 'wizard';
      slug: string;
      /** Draft load result; null while the draft is still loading. */
      hydrated: AiCommercial[] | null;
      /** Current draft token; create() needs it. */
      draftToken: string | null;
      committedIds: ReadonlyArray<string | null | undefined>;
    }
  | {
      surface: 'swap';
      slug: string;
      submissionId: string;
      /** Latched: fetch once when this first flips true. */
      active: boolean;
      committedIds: ReadonlyArray<string | null | undefined>;
    };

export interface AiCommercialsState {
  commercials: AiCommercial[];
  /** The commercial the provider should mount on; null when none is active. */
  activeId: string | null;
  active: AiCommercial | null;
  /** True once the surface knows its commercials (draft loaded / list fetched). */
  isLoaded: boolean;
  /** True while create() is in flight. */
  isCreating: boolean;
  error: string | null;
  /** 1-based position of a commercial on this surface (0 when unknown). */
  ordinalOf: (id: string | null | undefined) => number;
  /**
   * CTV-785: true when at least one commercial exists and every one has been
   * in the campaign at some point, so none is active. Gates Make another and
   * the campaign receipt (a finished commercial currently out of the campaign
   * does not unsettle the surface; its row offers Add to campaign).
   */
  allSettled: boolean;
  /**
   * CTV-785: finished commercials for the receipt, creation order, the active
   * one excluded: ever in the campaign, or rendered. Slot-pointer-only
   * commercials (saved AI creatives on the Edit tab) are included with no
   * render data.
   */
  finished: FinishedCommercial[];
  /** Commercials on the surface, listed or known only through committed slot pointers. */
  total: number;
  /** Insert a new commercial and make it active. Resolves null on failure. */
  create: () => Promise<AiCommercial | null>;
  /**
   * CTV-785: the access token of a commercial on this surface, for a side
   * editor session on a committed one. Null for ids the surface only knows as
   * slot pointers (saved AI creatives on the Edit tab take the rework path).
   */
  tokenOf: (id: string | null | undefined) => string | null;
  /** CTV-785: the provider persisted state for a commercial; keeps the snapshot current. */
  recordState: (id: string, patch: Partial<WaymarkInitial>) => void;
  /** CTV-785: a slot was just stamped with this commercial (mirrors the server stamp). */
  recordCommitted: (id: string) => void;
}

export function useAiCommercials(args: Args): AiCommercialsState {
  const [fetched, setFetched] = React.useState<AiCommercial[] | null>(null);
  const [created, setCreated] = React.useState<AiCommercial[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // CTV-785: what this session has learned about a commercial since it was
  // listed: the provider's persisted state (recordState) and the moment a
  // slot first carried it (recordCommitted). Merged over the listed snapshot
  // so the rules below never run on stale data.
  const [patches, setPatches] = React.useState<Record<string, CommercialPatch>>({});

  // Swap mode: one fetch per mount, when the tab first activates. The
  // modal latches activation, and the ref makes that a hook-level guarantee.
  const swapActive = args.surface === 'swap' ? args.active : false;
  const submissionId = args.surface === 'swap' ? args.submissionId : null;
  const fetchedRef = React.useRef(false);
  React.useEffect(() => {
    if (args.surface !== 'swap' || !swapActive || !submissionId) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    let settled = false;
    apiClient
      .post<{ commercials: AiCommercial[] }>('/waymark/commercials/list-for-submission', {
        submissionId,
      })
      .then((res) => {
        settled = true;
        if (!cancelled) setFetched(res.commercials);
      })
      .catch((e) => {
        settled = true;
        if (!cancelled) {
          setFetched([]);
          setError(e instanceof Error ? e.message : 'Failed to load your AI commercials');
        }
      });
    return () => {
      cancelled = true;
      // A cleanup before the response landed (StrictMode's double effect,
      // or a genuinely aborted mount) must let the next run fetch again.
      if (!settled) fetchedRef.current = false;
    };
  }, [args.surface, swapActive, submissionId]);

  const base: AiCommercial[] | null =
    args.surface === 'wizard' ? args.hydrated : fetched;
  const isLoaded = base != null;

  // Hydrated/fetched first (oldest first from the server), then anything
  // created in this session that the base does not already carry.
  const commercials = React.useMemo<AiCommercial[]>(() => {
    const seen = new Set<string>();
    const out: AiCommercial[] = [];
    for (const c of base ?? []) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(applyPatch(c, patches[c.id]));
    }
    for (const c of created) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(applyPatch(c, patches[c.id]));
    }
    return out;
  }, [base, created, patches]);

  const committed = React.useMemo(
    () => new Set(args.committedIds.filter((id): id is string => typeof id === 'string' && id.length > 0)),
    [args.committedIds],
  );

  // Committed commercials the surface knows about but the list does not carry
  // (the swap list is uncommitted-only, so a campaign's saved AI creatives
  // arrive purely as slot pointers). They count toward "there is at least one
  // commercial here" and come first in the ordinal order.
  const priorCommitted = React.useMemo(() => {
    const listed = new Set(commercials.map((c) => c.id));
    const out: string[] = [];
    for (const id of committed) if (!listed.has(id) && !out.includes(id)) out.push(id);
    return out;
  }, [commercials, committed]);

  // CTV-785: "was ever in the campaign": a slot carries it now, or the server
  // (or this session) stamped first_committed_at. Such a commercial is
  // finished business for the banner: never active again, re-addable from
  // its receipt row.
  const everCommitted = React.useCallback(
    (c: AiCommercial) => committed.has(c.id) || c.firstCommittedAt != null,
    [committed],
  );

  const activeId = React.useMemo<string | null>(() => {
    for (let i = commercials.length - 1; i >= 0; i--) {
      if (!everCommitted(commercials[i])) return commercials[i].id;
    }
    return null;
  }, [commercials, everCommitted]);

  const active = React.useMemo(
    () => commercials.find((c) => c.id === activeId) ?? null,
    [commercials, activeId],
  );

  const ordinalOf = React.useCallback(
    (id: string | null | undefined) => {
      if (!id) return 0;
      const prior = priorCommitted.indexOf(id);
      if (prior >= 0) return prior + 1;
      const idx = commercials.findIndex((c) => c.id === id);
      return idx < 0 ? 0 : priorCommitted.length + idx + 1;
    },
    [commercials, priorCommitted],
  );

  const total = commercials.length + priorCommitted.length;
  const allSettled = total > 0 && commercials.every(everCommitted);

  const finished = React.useMemo<FinishedCommercial[]>(() => {
    const out: FinishedCommercial[] = priorCommitted.map((id, i) => ({
      id,
      ordinal: i + 1,
      renderedUrl: null,
      videoId: null,
      everCommitted: true,
    }));
    commercials.forEach((c, idx) => {
      if (c.id === activeId) return;
      const ever = everCommitted(c);
      if (!ever && !c.initial.renderedUrl) return;
      out.push({
        id: c.id,
        ordinal: priorCommitted.length + idx + 1,
        renderedUrl: c.initial.renderedUrl,
        videoId: c.initial.videoId,
        everCommitted: ever,
      });
    });
    return out;
  }, [priorCommitted, commercials, activeId, everCommitted]);

  const argsRef = React.useRef(args);
  argsRef.current = args;

  const create = React.useCallback(async (): Promise<AiCommercial | null> => {
    const a = argsRef.current;
    setIsCreating(true);
    setError(null);
    try {
      const res =
        a.surface === 'wizard'
          ? await apiClient.post<AiCommercial>('/waymark/commercials', {
              slug: a.slug,
              draftToken: a.draftToken,
            })
          : await apiClient.post<AiCommercial>('/waymark/commercials/for-submission', {
              submissionId: a.submissionId,
            });
      setCreated((prev) => [...prev, res]);
      return res;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start your AI commercial');
      return null;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const tokenOf = React.useCallback(
    (id: string | null | undefined) =>
      id ? (commercials.find((c) => c.id === id)?.token ?? null) : null,
    [commercials],
  );

  const recordState = React.useCallback((id: string, patch: Partial<WaymarkInitial>) => {
    setPatches((prev) => ({
      ...prev,
      [id]: { ...prev[id], initial: { ...prev[id]?.initial, ...patch } },
    }));
  }, []);
  const recordCommitted = React.useCallback((id: string) => {
    setPatches((prev) =>
      prev[id]?.firstCommittedAt
        ? prev
        : { ...prev, [id]: { ...prev[id], firstCommittedAt: new Date().toISOString() } },
    );
  }, []);

  return {
    commercials,
    activeId,
    active,
    isLoaded,
    isCreating,
    error,
    ordinalOf,
    allSettled,
    finished,
    total,
    create,
    tokenOf,
    recordState,
    recordCommitted,
  };
}
